import { useCallback, useMemo } from 'react';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { useClubRoles } from '@features/auth/hooks/useClubRoles';
import { ClubSettingsPage, ClubSettingsPageTab } from '@features/club/pages/ClubSettingsPage';
import { SiteLayoutWidget } from '@features/shared/ui/SiteLayout/SiteLayoutWidget';
import { useIsAdmin } from '@features/auth/hooks/useIsAdmin';
import { PageTabsTab } from '@ui/components/PageTabs';

const querySchema = z.object({
  pageTab: z.enum(['participants', 'club', 'news', 'admin']).catch('participants').optional(),
});

type Query = z.infer<typeof querySchema>;

export const Route = createFileRoute('/club/$clubId_/settings')({
  component: Page,
  validateSearch: querySchema,
});

function Page() {
  const { clubId } = Route.useParams();
  const isAdmin = useIsAdmin();
  const { isManager } = useClubRoles(clubId);

  const { pageTab = isManager ? 'participants' : 'admin' } = Route.useSearch();
  const pageTabs: PageTabsTab<ClubSettingsPageTab>[] = useMemo(() => [
    { value: 'participants', label: 'Участники', disabled: !isManager },
    { value: 'club', label: 'Данные клуба', disabled: !isManager },
    { value: 'news', label: 'Новости', disabled: !isManager },
    { value: 'admin', label: 'Администратор', disabled: !isAdmin },
  ], [isAdmin, isManager]);

  const navigate = useNavigate({ from: Route.fullPath });

  const handleTabChange = useCallback(
    (newTab: Query['pageTab']) => {
      navigate({
        search: (prev) => ({ ...prev, pageTab: newTab }),
      });
    },
    [navigate],
  );
  if (!isManager && !isAdmin) {
    return <Navigate to={`/club/$clubId`} params={{ clubId }} />;
  }

  return (
    <SiteLayoutWidget hideCreateTournament clubId={clubId}>
      <ClubSettingsPage
        clubId={clubId}
        tabs={pageTabs}
        tab={pageTab}
        onTabChange={handleTabChange}
      />
    </SiteLayoutWidget>
  );
}
